import { cartConfig, reservesConfig, selectionlistConfig } from '../../config';
import * as types from '../constants';
import { globalErrorResponse, showNotification, TYPE_WARNING } from '../notifications/actions';
import { buildBulkRequestPathParamsAndData, determineGrouping } from '../productSelection/actions';
import { fetchSelectionlists } from '../orderlist/actions';
import { LineItemKey, ListRequest } from '../api';
import { AppDispatch, AppThunkAction, GetAppState } from '../index';
import {
    COPIES_LIST_FAILURE,
    COPIES_LIST_REQUEST,
    COPIES_LIST_RESET,
    COPIES_LIST_SUCCESS,
    COPIES_LIST_USAGE_CONDITIONS_UPDATE,
    CopiesListFailure,
    CopiesListRequest,
    CopiesListReset,
    CopiesListSuccess,
    CopiesListUpdateUsageConditions
} from './types';
import { CopiesListSuccessPayload } from './state';
import { invalidateCartSummaryQuery } from '../../api/shopping-cart/useCartSummaryQuery';
import { invalidateSelectionListProducts } from '../../shop/selectionlists/api/useSelectionListProducts';
import { invalidateSelectionListSummary } from '../../api/selection-list/useSelectionListSummary';
import { invalidateShoppingCartProducts } from '../../api/shopping-cart/useShoppingCartProducts';
import { invalidateCartGroupedItems } from '../../api/shopping-cart/useCartGroupedItems';
import { invalidateSelectionListGroupedItems } from '../../api/selection-list/useSelectionListGroupedItems';
import { invalidateCartAggregations } from '../../shop/cart/useCartAggregations';
import { invalidateSelectionListAggregations } from '../../shop/selectionlists/useSelectionListAggregations';
import { disableListLoading, enableListLoading } from '../listLoading/actions';
import i18n from '../../../i18n';
import { UpdateProductOptions } from '../products/actionsCart';
import { http } from '../../api/axios';

const endpointCartProduct = cartConfig().endpointProducts;
const endpointSelectionlistProduct = selectionlistConfig().endpointProducts;
const endpointReserveProductCopies = reservesConfig().endpointReserveProductCopies;

export const resetCopiesList = (): CopiesListReset => {
    return { type: COPIES_LIST_RESET };
};
const requestCopiesList = (): CopiesListRequest => {
    return { type: COPIES_LIST_REQUEST };
};
const failedCopiesList = (): CopiesListFailure => {
    return { type: COPIES_LIST_FAILURE, error: true };
};

export const copiesListUsageConditionUpdate = (productIds: string[]): CopiesListUpdateUsageConditions => {
    return { type: COPIES_LIST_USAGE_CONDITIONS_UPDATE, payload: productIds };
};

const receiveCopiesList = (resp: CopiesListSuccessPayload): CopiesListSuccess => {
    return { type: COPIES_LIST_SUCCESS, payload: resp };
};

function failCopiesList(error) {
    return (dispatch: AppDispatch) => {
        dispatch(globalErrorResponse(error));
        dispatch(failedCopiesList());
    };
}

const RESERVE_CHECKOUT_PATH = /^\/assistant\/reserve\/(.*?)\/checkout/;

function extractReserveListId(pathname: string) {
    const [, id] = RESERVE_CHECKOUT_PATH.exec(pathname) || [];
    return id;
}

export function fetchCopiesForProduct(productId: string): AppThunkAction<Promise<void>> {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        dispatch(requestCopiesList());

        const pathParams = {
            productId
        };

        // TODO refactor this quickly
        let endpoint = '';
        const pathname = getState().router.location?.pathname ?? '';
        if (pathname.startsWith(types.PATH_SELECTIONLISTS)) {
            endpoint = endpointSelectionlistProduct;
            pathParams['listId'] = getState().orderlist.id;
        } else if (pathname.match(RESERVE_CHECKOUT_PATH)) {
            pathParams['reserveListId'] = extractReserveListId(pathname);
            endpoint = endpointReserveProductCopies;
        } else if (pathname.startsWith(types.PATH_CART) || pathname.startsWith(types.PATH_CHECKOUT)) {
            endpoint = endpointCartProduct;
        } else if (pathname.startsWith(types.PATH_RESERVE)) {
            pathParams['reserveListId'] = getState().reserve.activeReservelist;
            endpoint = endpointReserveProductCopies;
        } else {
            throw new Error(`invalid pathname "${pathname}!`);
        }

        return http
            .get(endpoint, { pathParams })
            .then(response => {
                dispatch(
                    receiveCopiesList({
                        productId,
                        list: response.data.copies ? response.data.copies : response.data
                    } as CopiesListSuccessPayload)
                );
            })
            .catch(error => {
                if (error.response.status === 404) {
                    // if the resource is not found (no copies in cart for this product),
                    // remove product from cart store
                    invalidateShoppingCartProducts().then();
                } else {
                    dispatch(failCopiesList(error));
                }
            });
    };
}

export function removeCopy(productId: string, copyId: string): AppThunkAction<Promise<void>> {
    return (dispatch: AppDispatch, getState: GetAppState) => {
        const pathParams = {
            productId,
            copyId: `${copyId}`
        };

        let isCartRequest = false;
        let endpoint = '';
        const pathname = getState().router.location?.pathname ?? '';

        const listId = getState().orderlist.id ?? '';

        if (pathname.startsWith(types.PATH_SELECTIONLISTS)) {
            pathParams['listId'] = listId;
            endpoint = endpointSelectionlistProduct;
        } else if (pathname.startsWith(types.PATH_CART)) {
            endpoint = endpointCartProduct;
            isCartRequest = true;
        } else {
            throw new Error(i18n.t('notifications.removeCopy.error'));
        }

        dispatch(requestCopiesList());
        dispatch(enableListLoading());

        return http
            .delete(endpoint, {
                pathParams
            })
            .then(() => {
                dispatch(fetchCopiesForProduct(productId));

                if (isCartRequest) {
                    invalidateCartSummaryQuery().then();
                    invalidateShoppingCartProducts().then();
                    invalidateCartAggregations().then();
                    invalidateCartGroupedItems().then();
                } else {
                    invalidateSelectionListSummary(listId).then();
                    invalidateSelectionListProducts(listId).then();
                    invalidateSelectionListAggregations(listId).then();
                    invalidateSelectionListGroupedItems(listId).then();
                }
            })
            .catch(response => dispatch(failCopiesList(response)))
            .finally(() => {
                dispatch(disableListLoading());
            });
    };
}

export function copyAmountChange(
    productIdOrOptions: string | UpdateProductOptions | null | undefined,
    numCopies: number = 1,
    command: string = types.ACTION_SET,
    isOrderList: boolean = false
) {
    if (isNaN(numCopies)) {
        return () => {};
    }

    const {
        id: productId,
        price = { lendingModel: undefined, usageLimit: undefined, termLimit: undefined },
        subscriptionPeriod = undefined
    } = typeof productIdOrOptions === 'string' || !productIdOrOptions ? { id: productIdOrOptions } : productIdOrOptions;

    const { lendingModel, usageLimit, termLimit } = price ?? {};

    const usageCondition = {
        lendingModel,
        usageLimit,
        termLimit
    };

    const data = {
        ...usageCondition,
        action: command,
        amount: numCopies,
        targetSubscriptionPeriodType: subscriptionPeriod?.type
    } as ListRequest;

    return (dispatch: AppDispatch, state: GetAppState) => {
        const isShoppingCartRequest = !isOrderList;
        const listId = isOrderList ? state().orderlist.id : null;

        if (isShoppingCartRequest) {
            data['source'] = types.SOURCE_CART;
            if (productId) {
                copyAmountChangeCartProduct({ productId, usageCondition }, data, dispatch, state);
            } else {
                copyAmountChangeCartBulk(command, numCopies, dispatch, state);
            }
        } else if (listId) {
            data['source'] = types.SOURCE_SELECTIONLIST;
            data['sourceId'] = listId;
            if (productId) {
                copyAmountChangeListProduct(productId, listId, data, dispatch, state);
            } else {
                copyAmountChangeListBulk(listId, command, numCopies, dispatch, state);
            }
        }
    };
}

function copyAmountChangeCartProduct(lineItemKey: LineItemKey, data, dispatch, state) {
    const productId = lineItemKey.productId ?? '';

    dispatch(requestCopiesList());
    dispatch(enableListLoading());

    http.put(
        endpointCartProduct,
        { ...data, ...determineGrouping(state) },
        {
            pathParams: {
                productId
            }
        }
    )
        .then(() => {
            dispatch(fetchCopiesForProduct(productId));
            invalidateCartSummaryQuery().then();
            invalidateShoppingCartProducts().then();
            invalidateCartAggregations().then();
            invalidateCartGroupedItems().then();
        })
        .catch(error => {
            if (error.response.status === 200) {
                // this is, when our "runtime compensation" occurs,
                // but this is ok in this special case.
                dispatch(fetchCopiesForProduct(productId));
                invalidateCartSummaryQuery().then();
                invalidateCartGroupedItems().then();
            } else {
                dispatch(globalErrorResponse(error));
                invalidateShoppingCartProducts().then();
            }
        })
        .finally(() => {
            dispatch(disableListLoading());
        });
}

function copyAmountChangeListProduct(
    productId: string,
    listId: string,
    data,
    dispatch: AppDispatch,
    state: GetAppState
) {
    dispatch(requestCopiesList());
    dispatch(enableListLoading());

    const params = {
        ...determineGrouping(state)
    };

    // FIXME this should be a check for undefined rather than null
    // @ts-ignore
    if (null !== params.bundleRef) {
        // @ts-ignore
        data.bundleRef = params.bundleRef;
    }

    http.put(endpointSelectionlistProduct, data, {
        params,
        pathParams: {
            listId,
            productId
        }
    })
        .then(() => {
            dispatch(fetchCopiesForProduct(productId));
            invalidateSelectionListSummary(listId).then();
            invalidateSelectionListProducts(listId).then();
            invalidateSelectionListAggregations(listId).then();
            invalidateSelectionListGroupedItems(listId).then();
        })
        .catch(error => {
            dispatch(globalErrorResponse(error));
        })
        .finally(() => {
            dispatch(disableListLoading());
        });
}

function copyAmountChangeCartBulk(action, numCopies, dispatch, state) {
    const endpoint = cartConfig().endpointBulk;
    let requestdata;

    try {
        requestdata = buildBulkRequestPathParamsAndData(action, numCopies, state);
    } catch (error) {
        dispatch(showNotification(i18n.t('notifications.copyAmountChangeCartBulk.warning'), TYPE_WARNING));
        return;
    }

    const pathParams = requestdata.pathParams;
    const payload = requestdata.data;

    pathParams.source = types.SOURCE_QUANTITY;

    dispatch(enableListLoading());

    http.put(endpoint, payload, { pathParams })
        .then(() => {
            invalidateCartSummaryQuery().then();
            invalidateCartGroupedItems().then();
            invalidateShoppingCartProducts().then();
            invalidateCartAggregations().then();
            dispatch(showNotification(i18n.t('notifications.copyAmountChangeCartBulk.message')));
        })
        .catch(error => {
            dispatch(globalErrorResponse(error));
        })
        .finally(() => {
            dispatch(disableListLoading());
        });
}

function copyAmountChangeListBulk(listId, action, numCopies, dispatch, state) {
    const endpoint = selectionlistConfig().endpointBulk;
    let requestdata;
    try {
        requestdata = buildBulkRequestPathParamsAndData(action, numCopies, state);
    } catch (error) {
        dispatch(showNotification(i18n.t('notifications.copyAmountChangeListBulk.warning'), TYPE_WARNING));
        return;
    }
    const pathParams = requestdata.pathParams;
    const payload = requestdata.data;

    pathParams.listId = listId;
    pathParams.source = types.SOURCE_QUANTITY;

    dispatch(enableListLoading());

    http.put(endpoint, payload, { pathParams })
        .then(() => {
            dispatch(fetchSelectionlists());
            invalidateSelectionListGroupedItems(listId).then();
            invalidateSelectionListAggregations(listId).then();
            invalidateSelectionListSummary(listId).then();
            invalidateSelectionListProducts(listId).then();
            dispatch(showNotification(i18n.t('notifications.copyAmountChangeListBulk.message')));
        })
        .catch(error => {
            dispatch(globalErrorResponse(error));
        })
        .finally(() => {
            dispatch(disableListLoading());
        });
}
